/**
 * Readiness before conversion (internal, never rendered as a strip): orders and
 * consent/POA flip from EHR documentation, auth from the payer decision via the
 * EHR, the readiness call flips when the welcome-call wizard completes. Sets are
 * configured per payer; `gatesClear` decides when a hold converts — FFS on the
 * welcome call, MA when the auth lands.
 */
import type { ConfigState, GateKey, GateStatus, HoldReadiness, Payer } from "@caredna-marlin/utils/homeHealthDemo/state";

export const GATE_LABELS: Record<GateKey, string> = {
  orders: "Orders",
  readinessCall: "Welcome call",
  consent: "Consent / POA",
  auth: "Auth",
};

export const GATE_SOURCES: Record<GateKey, string> = {
  orders: "EHR documentation",
  readinessCall: "Welcome-call wizard",
  consent: "EHR documentation",
  auth: "Payer decision via the EHR's auth status",
};

export function gateSetFor(config: Pick<ConfigState, "gateSets">, payer: Payer): GateKey[] {
  return config.gateSets[payer];
}

/** Initial readiness when a referral is accepted. */
export function initialGates(config: Pick<ConfigState, "gateSets">, payer: Payer, opts: { ordersOnFile: boolean; consentOnFile?: boolean }): HoldReadiness {
  const gates: HoldReadiness = {};
  for (const key of gateSetFor(config, payer)) {
    switch (key) {
      case "orders":
        gates.orders = opts.ordersOnFile ? "done" : "needsAction";
        break;
      case "consent":
        gates.consent = opts.consentOnFile ? "done" : "pending";
        break;
      case "readinessCall":
        gates.readinessCall = "pending";
        break;
      case "auth":
        gates.auth = "pending";
        break;
    }
  }
  return gates;
}

export function setGate(readiness: HoldReadiness, key: GateKey, status: GateStatus): HoldReadiness {
  if (readiness[key] === undefined) return readiness; // not part of this payer's set
  return { ...readiness, [key]: status };
}

export function openGates(readiness: HoldReadiness): GateKey[] {
  return (Object.entries(readiness) as [GateKey, GateStatus][]).filter(([, s]) => s !== "done").map(([k]) => k);
}

/** A hold converts to a real SOC visit when every item in its readiness set is done. */
export function gatesClear(readiness: HoldReadiness): boolean {
  return openGates(readiness).length === 0;
}

export function gateSummary(readiness: HoldReadiness): { done: number; total: number; open: GateKey[] } {
  const total = Object.keys(readiness).length;
  const open = openGates(readiness);
  return { done: total - open.length, total, open };
}
